import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Stack } from '@mui/material';
import '../../../styles/DocumentDetails.css';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import DocumentSkeletonForm from '../../../components/DocumentSkeletonForm';
import ProformaInvoiceForm from '../components/ProformaInvoiceForm';
import { useGetProformaInvoiceHandler } from '../dataHandlers/getProformaInvoiceHandler';
import { useEditDraftProformaInvoiceHandler } from '../dataHandlers/editDraftProformaInvoiceHandler';
import { useCreateProformaInvoiceHandler } from '../dataHandlers/createProformaInvoiceHandler';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_PROFORMA_INVOICE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditProformaInvoicePage() {
  const { proformaInvoices: pi } = useTranslatedMessages();
  useDocumentTitle(pi?.pages?.edit?.document_title ?? 'Edit Proforma Invoice');
  const navigate = useNavigate();
  const location = useLocation();
  const { document_id } = useParams();
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { invoice, isLoading } = useGetProformaInvoiceHandler(document_id);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!hasEInvoicePermission(UPDATE_PROFORMA_INVOICE)) {
      showActionDeniedSnackbar();
      navigate('/sales/proforma-invoices', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  const onClickingBackButton = async () => {
    return navigate(location.state?.from || '/sales/proforma-invoices');
  };

  const onInvoiceEdited = () => {
    navigate('/sales/proforma-invoices');
  };

  const { onSubmit: onCreateProforma, error: editProformaError } = useCreateProformaInvoiceHandler({
    onSuccess: onInvoiceEdited,
  });

  const { onSubmit: onEditDraftProforma, error: editDraftProformaError } = useEditDraftProformaInvoiceHandler({
    onSuccess: onInvoiceEdited,
  });

  const onEditForm = async (data, isDraft) => {
    if (isDraft) {
      await onEditDraftProforma(data, document_id);
    } else {
      data = {
        ...data,
        id: document_id || null,
      };
      await onCreateProforma(data);
    }
  };

  if (isLoading || !invoice) {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <ProformaInvoiceForm
        onSubmit={onEditForm}
        onCancel={onClickingBackButton}
        initialData={invoice}
        isResubmit={true}
        apiErrors={editProformaError || editDraftProformaError}
      />
    </Stack>
  );
}
